import cn from 'classnames';

export enum LegacyButtonVariant {
  PRIMARY_LIGHT,
  PRIMARY_DARK,
  ANALYZE_BUTTON,
  APPLY_BUTTON,
  TERTIARY_DARK,
  PROMPT_CONFIRM_DESTRUCTIVE,
  PROMPT_CANCEL_DESTRUCTIVE,
}

type ButtonTheme = {
  base: string[];
  enabled: string[];
  disabled: string[];
};

// IMPORTANT! If using JIT mode with Tailwind, the full class names
// must be used. This means template string interpolation or string
// concatenation to build the names cannot be used.
export const buttonVariantThemes: Record<LegacyButtonVariant, ButtonTheme> = {
  [LegacyButtonVariant.PRIMARY_LIGHT]: {
    base: ['py-2 px-5 rounded-full'],
    enabled: [
      'text-primary',
      'border-secondary',
      'border-opacity-50',
      'bg-white',
      'hover:bg-highlight',
      'active:bg-tertiary',
      'focus-visible:bg-blue-400',
      'focus-visible:ring-tertiary',
      'focus-visible:ring-offset-white',
      'shadow-8px',
    ],
    disabled: [],
  },
  [LegacyButtonVariant.PRIMARY_DARK]: {
    base: ['py-2 px-4 rounded-full'],
    enabled: [
      'text-blue-100',
      'border-transparent',
      'bg-tertiary',
      'hover:bg-secondary',
      'active:bg-blue-1000',
      'focus-visible:bg-secondary',
      'focus-visible:ring-blue-300',
      'focus-visible:ring-offset-blue-1000',
    ],
    disabled: [],
  },
  [LegacyButtonVariant.ANALYZE_BUTTON]: {
    base: ['rounded', 'py-[1.15rem]'],
    enabled: ['text-blue-100', 'bg-royal-800', 'border-transparent'],
    disabled: [],
  },
  [LegacyButtonVariant.APPLY_BUTTON]: {
    base: ['rounded', 'py-[1.15rem]'],
    enabled: ['text-blue-100', 'bg-tertiary', 'border-transparent'],
    disabled: [],
  },
  [LegacyButtonVariant.TERTIARY_DARK]: {
    base: [
      'text-white',
      'border-transparent',
      'bg-raspberry-700',
      'hover:bg-raspberry-700',
      'active:bg-raspberry-800',
      'focus-visible:bg-raspberry-700',
      'focus-visible:ring-raspberry-300',
      'focus-visible:ring-offset-raspberry-1000',
      'py-2 px-5 rounded-full',
    ],
    enabled: [],
    disabled: [],
  },
  [LegacyButtonVariant.PROMPT_CONFIRM_DESTRUCTIVE]: {
    base: [
      'text-white',
      'border-transparent',
      'bg-red-700',
      'hover:bg-red-700',
      'active:bg-red-800',
      'focus-visible:bg-red-700',
      'focus-visible:ring-red-300',
      'focus-visible:ring-offset-red-1000',
      'py-2 px-5 rounded-full',
    ],
    enabled: [],
    disabled: [],
  },
  [LegacyButtonVariant.PROMPT_CANCEL_DESTRUCTIVE]: {
    base: [
      'text-white',
      'border-transparent',
      'bg-gray-500',
      'hover:bg-gray-500',
      'active:bg-gray-600',
      'focus-visible:bg-gray-500',
      'focus-visible:ring-gray-300',
      'focus-visible:ring-offset-gray-1000',
      'py-2 px-5 rounded-full',
    ],
    enabled: [],
    disabled: [],
  },
};

export const getButtonClassName = (
  variant: LegacyButtonVariant,
  disabled: boolean | undefined,
) => {
  const theme = buttonVariantThemes[variant];

  const className = cn(
    theme.base,
    disabled ? theme.disabled : theme.enabled,
    'focus-visible:outline-none focus:outline-none focus-visible:ring-1 focus-visible:ring-offset-2 border-1 font-bold text-sm whitespace-nowrap transition inline-block',
    { 'cursor-not-allowed bg-gray-200 text-gray-400': disabled },
  );

  return className;
};
